import React, { useEffect, useState, useMemo, useCallback } from 'react';
import ReactApexChart from 'react-apexcharts';
import dayjs from 'dayjs';
import {
  CurrencyDollarIcon,
  ShoppingBagIcon,
  ArrowTrendingUpIcon,
  BanknotesIcon,
  ReceiptPercentIcon,
  ChartBarIcon,
  TrophyIcon,
  SparklesIcon,
  BuildingStorefrontIcon,
  BuildingOffice2Icon,
  CalendarDaysIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import dashboardApi from '../../../services/dashboardApi';
import storeApi from '../../../services/storeApi';
import companyApi from '../../../services/companyApi';
import { setStartAndEndDate } from '../../../utils/setStartAndEndDate';
import { formatCurrency } from '../../../utils/baseUtils';
import {
  PageHeader,
  StatCard,
  SectionCard,
  TWButton,
  TWSelect,
  TWDateRangePicker,
  TWPagination,
  UserAvatar,
  EmptyState,
} from '../../../components/ui';

function buildDateRangePreset(value) {
  const now = dayjs();
  switch (value) {
    case 'today':
      return { start: now.startOf('day'), end: now.endOf('day') };
    case 'yesterday':
      return {
        start: now.subtract(1, 'day').startOf('day'),
        end: now.subtract(1, 'day').endOf('day'),
      };
    case 'last7':
      return {
        start: now.subtract(7, 'day').startOf('day'),
        end: now.endOf('day'),
      };
    case 'last30':
      return {
        start: now.subtract(30, 'day').startOf('day'),
        end: now.endOf('day'),
      };
    case 'last60':
    default:
      return {
        start: now.subtract(60, 'day').startOf('day'),
        end: now.endOf('day'),
      };
  }
}

const dateOptions = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'last7', label: 'Last 7 Days' },
  { value: 'last30', label: 'Last 30 Days' },
  { value: 'last60', label: 'Last 60 Days' },
  { value: 'custom', label: 'Custom Range' },
];

const formatNumber = (value) => (Number(value) || 0).toLocaleString('en-US');

const KPI_DEFS = [
  {
    key: 'grossSales',
    label: 'Gross Sales',
    icon: CurrencyDollarIcon,
    tone: 'blue',
    format: formatCurrency,
  },
  {
    key: 'orders',
    label: 'Orders',
    icon: ShoppingBagIcon,
    tone: 'violet',
    format: formatNumber,
  },
  {
    key: 'profit',
    label: 'Profit',
    icon: ArrowTrendingUpIcon,
    tone: 'emerald',
    format: formatCurrency,
  },
  {
    key: 'payout',
    label: 'Payout',
    icon: BanknotesIcon,
    tone: 'amber',
    format: formatCurrency,
  },
  {
    key: 'tax',
    label: 'Tax',
    icon: ReceiptPercentIcon,
    tone: 'rose',
    format: formatCurrency,
  },
];

const defaultRange = buildDateRangePreset('last7');
const INITIAL_DATE_RANGE = {
  preset: 'last7',
  startdate: setStartAndEndDate(defaultRange.start, 'startdate', '0'),
  enddate: setStartAndEndDate(defaultRange.end, 'enddate', '0'),
};

const ProductSkeletonRow = () => (
  <li className="report-row report-product-grid grid grid-cols-1 gap-3 px-5 py-3.5">
    <div className="flex items-center gap-3">
      <div className="skeleton-bar h-10 w-10 rounded-md" />
      <div className="min-w-0 flex-1 space-y-2">
        <div className="skeleton-bar h-3 w-32 rounded" />
        <div className="skeleton-bar h-3 w-20 rounded" />
      </div>
    </div>
    <div className="skeleton-bar h-3 w-12 rounded" />
    <div className="skeleton-bar h-4 w-20 justify-self-end rounded" />
  </li>
);

const ProductsTable = ({
  title,
  icon: Icon,
  loading,
  items,
  visibleItems,
  pagination,
  onPaginate,
  emptyCopy,
}) => (
  <section className="reveal overflow-hidden rounded-md border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800/70">
    <header className="flex items-center justify-between border-b border-slate-100 px-5 py-4 dark:border-slate-700/60">
      <div className="flex items-center gap-2">
        <Icon className="h-5 w-5 text-slate-400" />
        <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">
          {title}
        </h3>
      </div>
      {!loading && items.length > 0 && (
        <span className="rounded-md bg-slate-100 px-2.5 py-0.5 text-xs font-semibold text-slate-600 dark:bg-slate-700 dark:text-slate-300">
          {items.length}
        </span>
      )}
    </header>
    {loading ? (
      <ul className="divide-y divide-slate-100 dark:divide-slate-700/60">
        {[...Array(5)].map((_, i) => (
          <ProductSkeletonRow key={i} />
        ))}
      </ul>
    ) : items.length === 0 ? (
      <EmptyState
        variant="plain"
        icon={Icon}
        title="No data yet"
        description={emptyCopy}
      />
    ) : (
      <>
        <div className="overflow-x-auto">
          <div>
            <div className="report-product-grid hidden border-b border-slate-100 bg-slate-50/80 px-5 py-3 text-[0.65rem] font-semibold uppercase tracking-[0.15em] text-slate-500 dark:border-slate-700/60 dark:bg-slate-800/80 dark:text-slate-400 lg:block">
              <span className="justify-self-start">Product</span>
              <span className="justify-self-start">Quantity</span>
              <span className="justify-self-end">Total</span>
            </div>
            <ul className="divide-y divide-slate-100 dark:divide-slate-700/60">
              {visibleItems.map((item, idx) => (
                <li
                  key={item.id || idx}
                  className="report-row report-product-grid grid grid-cols-1 gap-3 px-5 py-3.5 hover:bg-slate-50/80 dark:hover:bg-slate-700/30"
                >
                  <div className="flex min-w-0 items-center gap-3">
                    {item.product?.imagepath ? (
                      <img
                        alt="product"
                        className="h-10 w-10 shrink-0 rounded-md object-cover ring-1 ring-slate-200 dark:ring-slate-700"
                        src={item.product.imagepath}
                      />
                    ) : (
                      <UserAvatar
                        name={item.product?.name || '?'}
                        size="md"
                        className="!rounded-md"
                      />
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold text-slate-900 dark:text-slate-100">
                        {item.product?.name || '—'}
                      </p>
                    </div>
                  </div>
                  <div className="text-sm text-slate-500 dark:text-slate-400">
                    <span className="text-[0.62rem] font-semibold uppercase tracking-[0.14em] text-slate-400 lg:hidden">
                      Qty
                    </span>
                    <span className="font-mono text-slate-700 dark:text-slate-200">
                      {formatNumber(item.qty)}
                    </span>
                  </div>
                  <div className="justify-self-start text-sm font-semibold text-slate-900 dark:text-slate-100 lg:justify-self-end">
                    <span className="text-[0.62rem] font-semibold uppercase tracking-[0.14em] text-slate-400 lg:hidden">
                      Total
                    </span>
                    {formatCurrency(item.total)}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
        {pagination.totalRecords > pagination.pageSize && (
          <div className="flex items-center justify-between border-t border-slate-100 bg-slate-50/50 px-5 py-3 dark:border-slate-700/60 dark:bg-slate-800/60">
            <span className="text-xs text-slate-500 dark:text-slate-400">
              {pagination.totalRecords} items
            </span>
            <TWPagination
              size="small"
              current={pagination.pageNumber}
              total={pagination.totalRecords}
              pageSize={pagination.pageSize}
              onChange={onPaginate}
            />
          </div>
        )}
      </>
    )}
  </section>
);

const CHART_BASE_OPTIONS = {
  chart: {
    type: 'area',
    height: 300,
    toolbar: { show: false },
    zoom: { enabled: false },
    fontFamily: 'inherit',
    background: 'transparent',
  },
  colors: ['#3b82f6'],
  dataLabels: { enabled: false },
  stroke: { curve: 'smooth', width: 2.5 },
  fill: {
    type: 'gradient',
    gradient: {
      shadeIntensity: 1,
      opacityFrom: 0.35,
      opacityTo: 0.05,
      stops: [0, 90, 100],
    },
  },
  xaxis: {
    type: 'category',
    categories: [],
    labels: { style: { colors: '#94a3b8', fontSize: '11px' } },
    axisBorder: { show: false },
    axisTicks: { show: false },
  },
  yaxis: {
    labels: {
      style: { colors: '#94a3b8', fontSize: '11px' },
      formatter: (val) =>
        val >= 1000 ? `$${(val / 1000).toFixed(1)}k` : `$${val}`,
    },
  },
  grid: {
    borderColor: 'rgba(148, 163, 184, 0.15)',
    strokeDashArray: 4,
    xaxis: { lines: { show: false } },
  },
  tooltip: { y: { formatter: (val) => formatCurrency(val) } },
  markers: { size: 0, hover: { size: 5 } },
};

export default function ReportAdminOverview() {
  const [dateRange, setDateRange] = useState(INITIAL_DATE_RANGE);
  const [companyId, setCompanyId] = useState('');
  const [storeIds, setStoreIds] = useState([]);
  const [companyList, setCompanyList] = useState([]);
  const [storeList, setStoreList] = useState([]);
  const [loading, setLoading] = useState({
    summary: true,
    chart: true,
    topItems: true,
    upsellItems: true,
  });
  const [summary, setSummary] = useState({
    grossSales: 0,
    orders: 0,
    profit: 0,
    payout: 0,
    tax: 0,
  });
  const [chartData, setChartData] = useState({ categories: [], values: [] });
  const [topItems, setTopItems] = useState([]);
  const [pagination, setPagination] = useState({
    pageNumber: 1,
    pageSize: 10,
    totalRecords: 0,
  });
  const [upsellItems, setUpsellItems] = useState([]);
  const [upsellPagination, setUpsellPagination] = useState({
    pageNumber: 1,
    pageSize: 10,
    totalRecords: 0,
  });

  const chartOptions = useMemo(
    () => ({
      ...CHART_BASE_OPTIONS,
      xaxis: {
        ...CHART_BASE_OPTIONS.xaxis,
        categories: chartData.categories,
      },
    }),
    [chartData.categories],
  );

  const chartSeries = useMemo(
    () => [{ name: 'Sales', data: chartData.values }],
    [chartData.values],
  );

  const storeIdsKey = storeIds.join(',');
  const queryParams = useMemo(
    () => ({
      startdate: dateRange.startdate,
      enddate: dateRange.enddate,
      companyid: companyId || undefined,
      storeids: storeIds,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dateRange.startdate, dateRange.enddate, companyId, storeIdsKey],
  );

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const res = await companyApi.list();
        setCompanyList([
          { label: 'All Companies', value: '' },
          ...res.data.data.map((c) => ({ label: c.name, value: c.id })),
        ]);
      } catch {
        setCompanyList([{ label: 'All Companies', value: '' }]);
      }
    };
    fetchCompanies();
  }, []);

  useEffect(() => {
    const fetchStores = async () => {
      try {
        setStoreList([]);
        const query = companyId ? `companyid=${companyId}` : '';
        const res = await storeApi.list(query);
        setStoreList(
          res.data.data.map((s) => ({ label: s.name, value: s.id })),
        );
      } catch {
        setStoreList([]);
      }
    };
    fetchStores();
  }, [companyId]);

  useEffect(() => {
    let cancelled = false;
    setLoading({
      summary: true,
      chart: true,
      topItems: true,
      upsellItems: true,
    });

    dashboardApi
      .getReports(queryParams)
      .then((res) => {
        if (cancelled) return;
        const counts = res.data?.counts?.[0] || {};
        setSummary({
          grossSales: counts.ordertotal || 0,
          orders: counts.ordercount || 0,
          profit: counts.profit || 0,
          payout: counts.payout || 0,
          tax: counts.tax || 0,
        });
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading((prev) => ({ ...prev, summary: false }));
      });

    dashboardApi
      .getOrdersDaily(queryParams)
      .then((res) => {
        if (cancelled) return;
        const data = res.data?.data || [];
        setChartData({
          categories: data.map((d) => d.date),
          values: data.map((d) => d.total),
        });
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading((prev) => ({ ...prev, chart: false }));
      });

    dashboardApi
      .getTopOrderItems(queryParams)
      .then((res) => {
        if (cancelled) return;
        const items = res.data?.data || [];
        setTopItems(items);
        setPagination((prev) => ({
          ...prev,
          totalRecords: items.length,
          pageNumber: 1,
        }));
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading((prev) => ({ ...prev, topItems: false }));
      });

    dashboardApi
      .getUpsellOrderItems(queryParams)
      .then((res) => {
        if (cancelled) return;
        const items = res.data?.data || [];
        setUpsellItems(items);
        setUpsellPagination((prev) => ({
          ...prev,
          totalRecords: items.length,
          pageNumber: 1,
        }));
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading((prev) => ({ ...prev, upsellItems: false }));
      });

    return () => {
      cancelled = true;
    };
  }, [queryParams]);

  const visibleItems = useMemo(() => {
    const start = (pagination.pageNumber - 1) * pagination.pageSize;
    return topItems.slice(start, start + pagination.pageSize);
  }, [topItems, pagination.pageNumber, pagination.pageSize]);

  const visibleUpsellItems = useMemo(() => {
    const start = (upsellPagination.pageNumber - 1) * upsellPagination.pageSize;
    return upsellItems.slice(start, start + upsellPagination.pageSize);
  }, [upsellItems, upsellPagination.pageNumber, upsellPagination.pageSize]);

  const handleCompanyChange = useCallback((val) => {
    setCompanyId(val ?? '');
    setStoreIds([]);
  }, []);

  const handleStoreChange = useCallback((val) => {
    setStoreIds(val || []);
  }, []);

  const handlePresetChange = useCallback((value) => {
    if (value === 'custom') {
      setDateRange((prev) => ({ ...prev, preset: value }));
      return;
    }
    const range = buildDateRangePreset(value);
    setDateRange({
      preset: value,
      startdate: setStartAndEndDate(range.start, 'startdate', '0'),
      enddate: setStartAndEndDate(range.end, 'enddate', '0'),
    });
  }, []);

  const handleCustomRange = useCallback((dates) => {
    if (!dates) return;
    setDateRange({
      preset: 'custom',
      startdate: setStartAndEndDate(dates[0], 'startdate', '0'),
      enddate: setStartAndEndDate(dates[1], 'enddate', '0'),
    });
  }, []);

  const hasFilters =
    Boolean(companyId) || storeIds.length > 0 || dateRange.preset !== 'last7';

  const resetFilters = () => {
    setCompanyId('');
    setStoreIds([]);
    const range = buildDateRangePreset('last7');
    setDateRange({
      preset: 'last7',
      startdate: setStartAndEndDate(range.start, 'startdate', '0'),
      enddate: setStartAndEndDate(range.end, 'enddate', '0'),
    });
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Reports"
        description="Performance overview across companies, stores, and timeframes."
      />

      <section className="reveal">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end">
          <div className="grid flex-1 grid-cols-2 gap-3 lg:grid-cols-3">
            <TWSelect
              label="Company"
              labelIcon={BuildingOffice2Icon}
              options={companyList}
              value={companyId || undefined}
              onChange={handleCompanyChange}
              placeholder="All Companies"
            />
            <TWSelect
              label="Stores"
              labelIcon={BuildingStorefrontIcon}
              mode="multiple"
              maxTagCount="responsive"
              options={storeList}
              value={storeIds}
              onChange={handleStoreChange}
              placeholder="All Stores"
            />
            <div className="col-span-2 lg:col-span-1 space-y-2">
              <TWSelect
                label="Date Range"
                labelIcon={CalendarDaysIcon}
                value={dateRange.preset}
                onChange={handlePresetChange}
                options={dateOptions}
              />
              {dateRange.preset === 'custom' && (
                <TWDateRangePicker
                  value={[dayjs(dateRange.startdate), dayjs(dateRange.enddate)]}
                  onChange={handleCustomRange}
                />
              )}
            </div>
          </div>
          {hasFilters && (
            <TWButton
              icon={TrashIcon}
              label="Reset"
              onClick={resetFilters}
              className="self-start lg:self-end"
            />
          )}
        </div>
      </section>

      <section className="reveal grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        {KPI_DEFS.map((def, index) => (
          <StatCard
            key={def.key}
            icon={def.icon}
            tone={def.tone}
            label={def.label}
            value={def.format(summary[def.key])}
            loading={loading.summary}
            style={{ animationDelay: `${index * 50}ms` }}
          />
        ))}
      </section>

      <SectionCard
        icon={<ChartBarIcon className="h-5 w-5 text-slate-400" />}
        title="Sales Trend"
        actions={
          <span className="rounded-md bg-blue-50 px-2.5 py-0.5 text-xs font-semibold text-blue-700 dark:bg-sky-500/10 dark:text-sky-400">
            {dateOptions.find((o) => o.value === dateRange.preset)?.label}
          </span>
        }
        bodyClassName="p-4"
      >
        {loading.chart ? (
          <div className="flex items-center justify-center py-20">
            <div className="skeleton-bar h-48 w-full rounded-md" />
          </div>
        ) : chartData.values.length === 0 ? (
          <EmptyState
            variant="plain"
            icon={ChartBarIcon}
            title="No sales data"
            description="No transactions were recorded in the selected period."
          />
        ) : (
          <ReactApexChart
            options={chartOptions}
            series={chartSeries}
            type="area"
            height={300}
          />
        )}
      </SectionCard>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <ProductsTable
          title="Top Products"
          icon={TrophyIcon}
          loading={loading.topItems}
          items={topItems}
          visibleItems={visibleItems}
          pagination={pagination}
          onPaginate={(pageNumber, pageSize) =>
            setPagination((prev) => ({ ...prev, pageNumber, pageSize }))
          }
          emptyCopy="No product sales in the selected period."
        />
        <ProductsTable
          title="Upsell Order Items"
          icon={SparklesIcon}
          loading={loading.upsellItems}
          items={upsellItems}
          visibleItems={visibleUpsellItems}
          pagination={upsellPagination}
          onPaginate={(pageNumber, pageSize) =>
            setUpsellPagination((prev) => ({
              ...prev,
              pageNumber,
              pageSize,
            }))
          }
          emptyCopy="No upsell items have been recorded yet."
        />
      </div>
    </div>
  );
}
